import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Contact, UserPlus, Check, Clock, Loader2, Smartphone, Link2, Copy, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function ContactsModal({ open, onClose }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [added, setAdded] = useState([]);
  const [inviteUrl, setInviteUrl] = useState("");
  const [inviting, setInviting] = useState(false);

  const supported = typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window;

  const makeInvite = async () => {
    setInviting(true);
    try {
      const { data } = await api.post("/invites");
      const url = `${window.location.origin}/login?invite=${data.code}`;
      setInviteUrl(url);
      try {
        if (navigator.share) await navigator.share({ title: "Join me on Xealth", text: "Come hang out with me on Xealth!", url });
        else { await navigator.clipboard.writeText(url); toast.success("Invite link copied!"); }
      } catch { /* share/clipboard dismissed or blocked — link is shown for manual copy */ }
    } catch {
      toast.error("Could not create invite link");
    } finally {
      setInviting(false);
    }
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Invite link copied!");
    } catch {
      toast.error("Copy failed — select and copy the link manually");
    }
  };

  const pick = async () => {
    setLoading(true);
    try {
      const contacts = await navigator.contacts.select(["name", "email"], { multiple: true });
      const emails = contacts.flatMap((c) => c.email || []);
      const { data } = await api.post("/contacts/match", { emails });
      setResult(data);
      if (data.matched_count === 0) toast.info("None of your contacts are on Xealth yet");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not access contacts");
    } finally {
      setLoading(false);
    }
  };

  const addFriend = async (id) => {
    try {
      await api.post(`/friends/request/${id}`);
      setAdded((a) => [...a, id]);
      qc.invalidateQueries({ queryKey: ["suggestions"] });
      toast.success("Friend request sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const close = () => { setResult(null); setAdded([]); onClose(); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="contacts-modal" className="bg-card border-2 border-[#6C0BA9] rounded-3xl max-w-sm">
        <DialogTitle className="font-heading text-lg font-extrabold flex items-center gap-2">
          <Contact className="w-5 h-5 text-[#C084FC]" /> Find Friends from Contacts
        </DialogTitle>

        {!result ? (
          <div className="text-center py-4 space-y-4">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-[#6C0BA9]/15 flex items-center justify-center">
              <Smartphone className="w-8 h-8 text-[#C084FC]" />
            </div>
            {supported ? (
              <>
                <p className="text-sm text-muted-foreground">Pick contacts from your phone and we'll find who's already on Xealth. Emails are only used to match — never stored.</p>
                <Button data-testid="pick-contacts-btn" onClick={pick} disabled={loading} className="w-full h-11 rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white font-semibold gap-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Contact className="w-4 h-4" />Choose Contacts</>}
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Your browser doesn't support the Contact Picker. Open Xealth in Chrome on an Android phone to import contacts.</p>
            )}

            <div className="pt-4 border-t border-border">
              <div className="flex items-center gap-2 text-sm font-semibold mb-2 justify-center"><Link2 className="w-4 h-4 text-[#C084FC]" />Or invite anyone by link</div>
              {inviteUrl ? (
                <div className="flex items-center gap-2 bg-input border border-border rounded-xl p-1.5">
                  <span className="text-xs text-muted-foreground truncate flex-1 pl-2">{inviteUrl}</span>
                  <button data-testid="copy-invite-btn" onClick={copyInvite} className="w-8 h-8 shrink-0 rounded-lg bg-[#6C0BA9] hover:bg-[#8012C6] text-white flex items-center justify-center"><Copy className="w-4 h-4" /></button>
                </div>
              ) : (
                <Button data-testid="invite-link-btn" onClick={makeInvite} disabled={inviting} variant="outline" className="w-full h-11 rounded-xl border-[#6C0BA9]/40 gap-2">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Share2 className="w-4 h-4" />Create Invite Link</>}
                </Button>
              )}
              <p className="text-[11px] text-muted-foreground text-center mt-2">New friends who join from your link are added automatically.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground mb-2">{result.matched_count} of your contacts are on Xealth</p>
            <div className="max-h-[46vh] overflow-y-auto space-y-1">
              {result.matched.map((u) => {
                const isAdded = added.includes(u.user_id) || u.friend_status === "outgoing";
                const isFriend = u.friend_status === "friends";
                return (
                  <div key={u.user_id} data-testid={`contact-match-${u.user_id}`} className="flex items-center gap-3 px-1 py-2">
                    <Avatar user={u} size={40} onClick={() => { navigate(`/profile/${u.user_id}`); close(); }} />
                    <span className="text-sm font-medium flex-1 flex items-center gap-1">{u.name}{u.verified && <VerifiedBadge size={12} />}</span>
                    {isFriend ? (
                      <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Check className="w-4 h-4" />Friends</span>
                    ) : isAdded ? (
                      <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Clock className="w-4 h-4" />Sent</span>
                    ) : (
                      <Button data-testid={`contact-add-${u.user_id}`} onClick={() => addFriend(u.user_id)} size="sm" className="rounded-full bg-[#6C0BA9] hover:bg-[#8012C6] text-white h-8 gap-1"><UserPlus className="w-3.5 h-3.5" />Add</Button>
                    )}
                  </div>
                );
              })}
            </div>
            <Button data-testid="contacts-rescan-btn" variant="outline" onClick={() => setResult(null)} className="w-full rounded-xl border-border mt-2">Scan again</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
